import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');
  const [sortBy, setSortBy] = useState<'hot' | 'new' | 'rating'>('hot');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const comics = getComicsByCategory(activeSlug);

  const sortedComics = [...comics].sort((a, b) => {
    if (sortBy === 'hot') return b.views - a.views;
    if (sortBy === 'new') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    return b.rating - a.rating;
  });

  if (!category) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 pt-16">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background-50 pt-16">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${category.name}韩漫` },
          ]}
        />

        <header className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
            {category.name}<span className="text-primary-600">韩漫</span>
          </h1>
          <p className="text-sm text-foreground-500 mt-2">{category.description}</p>
        </header>

        <nav className="flex flex-wrap items-center gap-2 mb-8" aria-label="分类筛选">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              onClick={() => setActiveSlug(cat.slug)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
            </Link>
          ))}
        </nav>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-foreground-500">
            共找到 <strong className="text-foreground-900">{comics.length}</strong> 部{category.name}韩漫
          </p>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            {[
              { key: 'hot' as const, label: '最热' },
              { key: 'new' as const, label: '最新' },
              { key: 'rating' as const, label: '评分' },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => setSortBy(item.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  sortBy === item.key
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {sortedComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>

        {sortedComics.length === 0 && (
          <div className="text-center py-20">
            <i className="ri-inbox-line text-5xl text-foreground-300"></i>
            <p className="text-foreground-500 mt-4">该分类暂无漫画</p>
          </div>
        )}
      </div>
    </main>
  );
}